import { canAccessFeature, TierFeatureKey } from '../constants/planFeatures';
import type { Academy } from '../types';

/** API error payload code when path-mode public slug lookup is blocked by an active branded subdomain. */
export const PUBLIC_ACADEMY_BRANDED_SUBDOMAIN_CODE = 'BRANDED_SUBDOMAIN_ACTIVE' as const;

export type AcademyPublicUrlEnv = 'production' | 'staging' | 'development';

type AcademyHostMode = 'path' | 'subdomain';

export interface AcademyPublicUrlOptions {
  /** When omitted, inferred from env vars in Node or `window` in browser. */
  env?: AcademyPublicUrlEnv;
  /** Override web app origin for path-based URLs (e.g. https://app.courtsync.io). */
  webAppOrigin?: string;
  /** Override tenant subdomain base domain (e.g. courtsync.io). */
  subdomainBaseDomain?: string;
}

function normalizeOrigin(origin: string): string {
  return origin.replace(/\/$/, '');
}

function isLocalDevHostname(host: string): boolean {
  return host === 'localhost' || host.endsWith('.localhost');
}

function isStagingHostname(host: string): boolean {
  return host === 'dev.courtsync.io' || host.endsWith('.dev.courtsync.io');
}

function inferEnv(): AcademyPublicUrlEnv {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    const web = process.env.WEB_APP_URL ?? '';
    if (web.includes('dev.') || web.includes('-dev')) return 'staging';
    return 'production';
  }
  if (typeof globalThis !== 'undefined') {
    const loc = (globalThis as { location?: { hostname?: string; port?: string; origin?: string } }).location;
    const host = loc?.hostname;
    if (host) {
      if (isLocalDevHostname(host)) {
        return 'development';
      }
      if (isStagingHostname(host)) {
        return 'staging';
      }
      return 'production';
    }
  }
  return 'development';
}

function defaultWebAppOrigin(env: AcademyPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'https://app.courtsync.io';
    case 'staging':
      return 'https://app.dev.courtsync.io';
    default: {
      const origin = (globalThis as { location?: { origin?: string } }).location?.origin;
      return origin ? normalizeOrigin(origin) : 'http://localhost:3000';
    }
  }
}

function defaultSubdomainBaseDomain(env: AcademyPublicUrlEnv): string {
  switch (env) {
    case 'production':
      return 'courtsync.io';
    case 'staging':
      return 'dev.courtsync.io';
    default:
      return 'localhost';
  }
}

/** Base domain for tenant subdomains in the current environment (e.g. courtsync.io). */
export function academySubdomainBaseDomain(options: AcademyPublicUrlOptions = {}): string {
  const env = options.env ?? inferEnv();
  return options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);
}

/** Human-readable branded host for UI copy (e.g. elite.courtsync.io or elite.localhost:3000). */
export function formatAcademySubdomainHost(
  label: string,
  options: AcademyPublicUrlOptions = {}
): string {
  const env = options.env ?? inferEnv();
  const baseDomain = academySubdomainBaseDomain(options);
  const normalized = label.trim().toLowerCase();
  if (env === 'development' && baseDomain === 'localhost') {
    const port = (globalThis as { location?: { port?: string } }).location?.port;
    const portSuffix = port ? `:${port}` : ':3000';
    return `${normalized}.localhost${portSuffix}`;
  }
  return `${normalized}.${baseDomain}`;
}

export function isBrandedSubdomainActive(academy: Pick<Academy, 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>): boolean {
  if (!academy.subdomain?.trim() || academy.subdomainEnabled !== true) {
    return false;
  }
  const tier = academy.subscriptionTier ?? 'starter';
  return canAccessFeature(tier, TierFeatureKey.BRANDED_SUBDOMAIN);
}

/**
 * Public site origin for an academy: branded subdomain when enabled + Pro+, else path-based app host.
 */
export function academyPublicOrigin(
  academy: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>,
  options: AcademyPublicUrlOptions = {}
): string {
  const env = options.env ?? inferEnv();
  const webAppOrigin = normalizeOrigin(options.webAppOrigin ?? defaultWebAppOrigin(env));
  const baseDomain = options.subdomainBaseDomain ?? defaultSubdomainBaseDomain(env);

  if (isBrandedSubdomainActive(academy)) {
    const label = academy.subdomain!.trim().toLowerCase();
    if (env === 'development' && baseDomain === 'localhost') {
      const port = (globalThis as { location?: { port?: string } }).location?.port;
      const portSuffix = port ? `:${port}` : ':3000';
      return `http://${label}.localhost${portSuffix}`;
    }
    return `https://${label}.${baseDomain}`;
  }

  return webAppOrigin;
}

/**
 * Path prefix for academy-scoped routes on the current host mode.
 * Path mode: `/academy/:slug`; subdomain mode: `` (empty).
 */
export function academyPublicPathPrefix(
  academy: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>,
  hostMode: 'path' | 'subdomain'
): string {
  // On a branded subdomain host the academy is identified by hostname, not URL path.
  if (hostMode === 'subdomain') {
    return '';
  }
  return `/academy/${academy.slug}`;
}

/** Build a relative academy-scoped path (e.g. `/lessons` or `/academy/slug/lessons`). */
export function academyPublicPath(
  academy: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>,
  subpath: string,
  hostMode: 'path' | 'subdomain' = 'path'
): string {
  const prefix = academyPublicPathPrefix(academy, hostMode);
  const normalized = subpath.startsWith('/') ? subpath : `/${subpath}`;
  if (!prefix) return normalized;
  return `${prefix}${normalized}`;
}

/** Absolute public URL for an academy path. */
export function academyPublicBaseUrl(
  academy: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>,
  subpath = '',
  options: AcademyPublicUrlOptions = {},
  hostMode: 'path' | 'subdomain' = 'path'
): string {
  const origin = academyPublicOrigin(academy, options);
  const effectiveHostMode: AcademyHostMode =
    isBrandedSubdomainActive(academy) ? 'subdomain' : hostMode;
  const path = subpath
    ? academyPublicPath(academy, subpath, effectiveHostMode)
    : academyPublicPathPrefix(academy, effectiveHostMode);
  return path ? `${origin}${path}` : origin;
}
